import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { LocalGovernment, from, is } from '@the-real-insight/tri-model';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Slider } from '@/components/ui/slider';
import AddressPicker from '../components/AddressPicker';
import { userAPI } from '../UserAPI';
import {
    AreaKind,
    AreaSelection,
    DEFAULT_RADIUS_KM,
    MAX_RADIUS_KM,
    areaGeometryFromEntity,
    circleGeometry,
} from './areaGeometry';
import { sectionHint } from './optionStyles';

/**
 * The area the heatmap is drawn for: a radius around an address, a postal code district, or a town.
 *
 * The address case uses the same picker as adding a property to a project, so an address means the
 * same thing in both places. The other two resolve a name to an entity that already carries an
 * outline — nothing here draws a boundary of its own.
 */

interface Properties {
    area: AreaSelection | null;
    onAreaChange: (area: AreaSelection | null) => void;
}

export default function AreaSection({ area, onAreaChange }: Properties) {
    const { t } = useTranslation();
    const kindFieldId = useId();
    const postalCodeFieldId = useId();
    const localityFieldId = useId();
    const [kind, setKind] = useState<AreaKind>(area?.kind ?? 'address');
    const [address, setAddress] = useState<any>(null);
    const [radiusKm, setRadiusKm] = useState(area?.radiusKm ?? DEFAULT_RADIUS_KM);
    const [postalCode, setPostalCode] = useState('');
    const [locality, setLocality] = useState('');
    const [busy, setBusy] = useState(false);
    const [notFound, setNotFound] = useState<string | null>(null);

    // An area can also arrive from outside — clicking a property on the map sets one. The controls
    // follow it, otherwise the radio would still say "PLZ-Gebiet" while a circle is on the map.
    useEffect(() => {
        if (!area) return;
        setKind(area.kind);
        if (area.radiusKm !== undefined) setRadiusKm(area.radiusKm);
        setAddress(null);
    }, [area]);

    /**
     * The centre the radius applies to: the address just picked, or — when the area came from a
     * marker — that property. Without the second, dragging the slider after a marker click would
     * find no address and wipe the area instead of resizing it.
     */
    const currentCentre = (): { lat: number; lon: number; label: string } | null => {
        if (address && Number.isFinite(address.lat) && Number.isFinite(address.lon)) {
            return {
                lat: address.lat,
                lon: address.lon,
                label: [address.street, address.streetNumber].filter(Boolean).join(' '),
            };
        }
        if (area?.kind === 'address' && area.centre) {
            return { ...area.centre, label: area.label.replace(/\s+—\s+[\d.,]+\s*km$/, '') };
        }
        return null;
    };

    const applyRadius = (radius: number) => {
        const centre = currentCentre();
        if (!centre) {
            onAreaChange(null);
            return;
        }

        onAreaChange({
            kind: 'address',
            label: `${centre.label} — ${radius} km`,
            geometry: circleGeometry(centre.lat, centre.lon, radius),
            centre: { lat: centre.lat, lon: centre.lon },
            radiusKm: radius,
        });
    };

    const applyAddress = (picked: any, radius: number) => {
        if (!picked || !Number.isFinite(picked.lat) || !Number.isFinite(picked.lon)) {
            onAreaChange(null);
            return;
        }

        onAreaChange({
            kind: 'address',
            label: `${[picked.street, picked.streetNumber].filter(Boolean).join(' ')} — ${radius} km`,
            geometry: circleGeometry(picked.lat, picked.lon, radius),
            centre: { lat: picked.lat, lon: picked.lon },
            radiusKm: radius,
        });
    };

    const resolvePostalCode = async () => {
        const code = postalCode.trim();
        if (!code) return;

        setBusy(true);
        setNotFound(null);
        try {
            const entity = await userAPI.getPostalCode(code);
            const geometry = areaGeometryFromEntity(entity);

            if (!geometry) {
                setNotFound(t('heatmap.area.postalCodeNotFound', { code }));
                onAreaChange(null);
                return;
            }

            onAreaChange({
                kind: 'postalCode',
                label: [code, entity?.city].filter(Boolean).join(' '),
                geometry,
            });
        } catch (error) {
            console.error('Could not resolve the postal code', error);
            setNotFound(t('heatmap.area.postalCodeNotFound', { code }));
        } finally {
            setBusy(false);
        }
    };

    const resolveLocality = async () => {
        const name = locality.trim();
        if (!name) return;

        setBusy(true);
        setNotFound(null);
        try {
            const found = await userAPI.query(from(LocalGovernment.type).where(is('name').equal(name)));
            // Several administrative levels can share a name (town and its Kreis); the one with an
            // outline wins, and among those the first — picking by area would need the geometry of
            // all of them, which is exactly what we are trying not to load.
            const entity = (found ?? []).map((candidate: any) => candidate).find((candidate: any) => !!areaGeometryFromEntity(candidate));
            const geometry = areaGeometryFromEntity(entity);

            if (!geometry) {
                setNotFound(t('heatmap.area.localityNotFound', { name }));
                onAreaChange(null);
                return;
            }

            onAreaChange({ kind: 'locality', label: entity.name ?? name, geometry });
        } catch (error) {
            console.error('Could not resolve the locality', error);
            setNotFound(t('heatmap.area.localityNotFound', { name }));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div>
            <RadioGroup
                value={kind}
                aria-label={t('heatmap.area.title')}
                onValueChange={(value) => {
                    setKind(value as AreaKind);
                    setNotFound(null);
                    onAreaChange(null);
                }}
            >
                {(
                    [
                        { key: 'address', text: t('heatmap.area.address') },
                        { key: 'postalCode', text: t('heatmap.area.postalCode') },
                        { key: 'locality', text: t('heatmap.area.locality') },
                    ] as const
                ).map((choice) => (
                    <div key={choice.key} className="flex items-center gap-2">
                        <RadioGroupItem value={choice.key} id={`${kindFieldId}-${choice.key}`} />
                        <Label htmlFor={`${kindFieldId}-${choice.key}`} className="cursor-pointer font-normal">
                            {choice.text}
                        </Label>
                    </div>
                ))}
            </RadioGroup>

            {kind === 'address' ? (
                <div className="mt-5">
                    <AddressPicker
                        setAddress={(picked: any) => {
                            setAddress(picked);
                            applyAddress(picked, radiusKm);
                        }}
                    />
                    <div className="mt-3 flex flex-col gap-1.5">
                        {/* A span, not a `Label`: the element a `for` would point at is the slider's
                            root, and only the thumb inside it carries the slider role. */}
                        <div className="flex items-baseline justify-between gap-2">
                            <span className="text-sm font-medium leading-none">{t('heatmap.area.radius')}</span>
                            <span className="text-xs text-muted-foreground">{`${radiusKm} km`}</span>
                        </div>
                        <Slider
                            min={0.25}
                            max={MAX_RADIUS_KM}
                            step={0.25}
                            value={[radiusKm]}
                            onValueChange={(next) => {
                                const value = next[0] ?? radiusKm;
                                setRadiusKm(value);
                                applyRadius(value);
                            }}
                        />
                    </div>
                </div>
            ) : (
                <></>
            )}

            {kind === 'postalCode' ? (
                <div className="mt-5">
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor={postalCodeFieldId}>{t('heatmap.area.postalCode')}</Label>
                        <Input
                            id={postalCodeFieldId}
                            value={postalCode}
                            placeholder="60594"
                            onChange={(event) => setPostalCode(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') void resolvePostalCode();
                            }}
                        />
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        className="mt-3 w-full sm:w-auto"
                        disabled={busy || !postalCode.trim()}
                        onClick={resolvePostalCode}
                    >
                        {t('heatmap.area.apply')}
                    </Button>
                </div>
            ) : (
                <></>
            )}

            {kind === 'locality' ? (
                <div className="mt-5">
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor={localityFieldId}>{t('heatmap.area.locality')}</Label>
                        <Input
                            id={localityFieldId}
                            value={locality}
                            placeholder="Frankfurt am Main"
                            onChange={(event) => setLocality(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') void resolveLocality();
                            }}
                        />
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        className="mt-3 w-full sm:w-auto"
                        disabled={busy || !locality.trim()}
                        onClick={resolveLocality}
                    >
                        {t('heatmap.area.apply')}
                    </Button>
                </div>
            ) : (
                <></>
            )}

            {busy ? (
                <div role="status" className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    {t('global.searching')} …
                </div>
            ) : (
                <></>
            )}

            {notFound ? <div className="mt-3 text-sm text-destructive">{notFound}</div> : <></>}

            {area ? <div className={cn(sectionHint, 'mt-3')}>{area.label}</div> : <></>}
        </div>
    );
}
